/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TablePaginationProps,
  TableRow,
  Typography,
} from "@material-ui/core";
import ButtonWithLoading from "components/ButtonWithLoading";
import PaginationActions from "./PaginationActions";
import RowsLoader from "./RowsLoader";
import { useStyles } from "../styled";

import { EmailFlowLog } from "@dashboard-v3/api";
import { State } from "../../types";
import { useEmailLogContext } from "pages/EmailFlowLogs/EmailLogProvider";

interface Props {
  searchState: State;
  updateState: (update: Partial<State>) => void;
  onGetMore: () => void;
}

export default function LogsTable(props: Props) {
  const { t } = useTranslation("emailFlowLogs");
  const classes = useStyles();
  const history = useHistory();
  const { updateState } = useEmailLogContext();
  const { searchState, onGetMore } = props;
  const { status, logs, hasMore, page, rowsPerPage } = searchState;
  const hasResults = Boolean(logs.length);
  const showEmptyMsg = !hasResults && status === "IDLE";
  const isLastPage = page >= Math.ceil(logs.length / rowsPerPage) - 1;
  const rowsOpts = [
    10,
    20,
    50,
    100,
    150,
    200,
    250,
    { label: t("common:all"), value: logs?.length || 0 },
  ];

  const getDate = (timestamp: number) => {
    const format = "MMM D, YYYY, h:mm:ss A";
    return dayjs(timestamp).format(format);
  };

  const getVisibleRows = () => {
    const leftBoundary = page * rowsPerPage;
    const rightBoundary = page * rowsPerPage + rowsPerPage;

    return logs.slice(leftBoundary, rightBoundary);
  };

  const onRowsPerPageChange: TablePaginationProps["onRowsPerPageChange"] =
    e => {
      const { value } = e.target;
      const numValue = parseInt(value, 10);
      updateState({ rowsPerPage: numValue, page: 0 });
    };

  const onLogSelect = (log: EmailFlowLog) => () => {
    const params = new URLSearchParams(window.location.search);
    updateState({ selectedEmail: log });
    history.push(`/emailflow-logs/${log.msgUID}?${params.toString()}`);
  };

  return (
    <TableContainer component={Paper} style={{ marginTop: "20px" }}>
      <Table>
        <TableHead className={classes.tableHead}>
          <TableCell className={classes.tableHeadContent} align="left">
            {t("search.resultsHeader.subject")}
          </TableCell>
          <TableCell className={classes.tableHeadContent} align="left">
            {t("search.resultsHeader.from")}
          </TableCell>
          <TableCell className={classes.tableHeadContent} align="left">
            {t("search.resultsHeader.date")}
          </TableCell>
        </TableHead>
        <TableBody>
          {status === "SEARCHING" && <RowsLoader rows={10} />}
          {showEmptyMsg && (
            <TableCell colSpan={3} align="center">
              <Typography variant="caption" color="textSecondary">
                {t("search.emptyResults")}
              </Typography>
            </TableCell>
          )}
          {hasResults &&
            getVisibleRows().map(log => (
              <TableRow
                key={log.msgUID}
                className={classes.emailRow}
                onClick={onLogSelect(log)}
              >
                <TableCell
                  component="th"
                  scope="row"
                  style={{ color: "#009ed2" }}
                >
                  <Typography
                    style={{ fontSize: "14px", wordBreak: "break-all" }}
                  >
                    {log.subject}
                  </Typography>
                </TableCell>
                <TableCell component="th" scope="row">
                  <Typography
                    style={{ fontSize: "14px", wordBreak: "break-all" }}
                  >
                    {log.from}
                  </Typography>
                </TableCell>
                <TableCell component="th" scope="row">
                  <Typography
                    style={{ fontSize: "14px", whiteSpace: "nowrap" }}
                  >
                    {getDate(log.createdAt)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
        {hasResults && (
          <>
            <TablePagination
              className={classes.pagination}
              page={page}
              rowsPerPage={rowsPerPage}
              count={logs.length}
              rowsPerPageOptions={rowsOpts}
              labelRowsPerPage={t("search.results.rowsPerPage")}
              onRowsPerPageChange={onRowsPerPageChange}
              onPageChange={(_, page) => updateState({ page })}
              SelectProps={{ native: true }}
              ActionsComponent={props => (
                <PaginationActions
                  {...props}
                  getMoreBtn={
                    <ButtonWithLoading
                      variant="contained"
                      size="small"
                      disableElevation
                      loading={status === "GETTING_MORE_LOGS"}
                      onClick={onGetMore}
                      disabled={!hasMore || !isLastPage}
                    >
                      {t("search.results.getMoreBtn")}
                    </ButtonWithLoading>
                  }
                />
              )}
            />
          </>
        )}
      </Table>
    </TableContainer>
  );
}
